import { useEffect, useState } from "react";
import { authToken } from "../lib/api";

const ENDPOINT = "/employee-locations/me";
const UPDATE_MS = 60_000;
const MAX_POSITION_AGE_MS = 30_000;
const PREFERENCE_PREFIX = "staff-location-paused:";
const preferenceMemory = new Map();
const preferenceKey = (username) => `${PREFERENCE_PREFIX}${username}`;

export function locationSharingPaused(username) {
  if (!username) return false;
  if (preferenceMemory.has(username)) return preferenceMemory.get(username);
  try { return localStorage.getItem(preferenceKey(username)) === "1"; }
  catch { return false; }
}

export function setLocationSharingPaused(username, paused) {
  if (!username) return;
  const value = !!paused;
  preferenceMemory.set(username, value);
  try { localStorage.setItem(preferenceKey(username), value ? "1" : "0"); } catch { /* private browsing: current-tab choice still works */ }
  window.dispatchEvent(new CustomEvent("staff-location-preference", { detail: { username, paused: value } }));
}

export function canShareStaffLocation(user, allowed) {
  // External self-service accounts can reach HR routes through a legacy `*`
  // default; they are never employee devices for this location feature.
  const externalRoles = ["subcontractor", "owner", "vendor"];
  return !!(user?.username && allowed && !user.impersonatedBy && !user._impersonatedBy
    && ![user.role, user.baseRole].some((role) => externalRoles.includes(role)));
}

export function useLocationSharingPaused(username) {
  const [, refreshPreference] = useState(0);
  const paused = locationSharingPaused(username);
  useEffect(() => {
    const onPreference = (event) => {
      if (event.detail?.username === username) refreshPreference((value) => value + 1);
    };
    const onStorage = (event) => {
      if (event.key !== preferenceKey(username)) return;
      preferenceMemory.delete(username);
      refreshPreference((value) => value + 1);
    };
    window.addEventListener("staff-location-preference", onPreference);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("staff-location-preference", onPreference);
      window.removeEventListener("storage", onStorage);
    };
  }, [username]);
  return paused;
}
const newClientId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `location-${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** Share only fresh browser positions while this employee is logged in and the page is visible. */
export function useStaffLocationSharing(api, user, allowed) {
  const username = user?.username || "";
  const eligible = canShareStaffLocation(user, allowed);
  const paused = useLocationSharingPaused(username);

  useEffect(() => {
    if (!eligible || paused || !navigator.geolocation) return undefined;

    const clientId = newClientId(); // independent tab/account lease; never persisted
    const token = authToken(); // capture before logout clears the global Axios header
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    let active = true;
    let visible = document.visibilityState !== "hidden";
    let denied = false;
    let locating = false;
    let uploading = false;
    let generation = 0;
    let published = false;
    let permission = null;

    const changed = () => window.dispatchEvent(new Event("staff-location-updated"));
    const remove = (keepalive = false, force = false) => {
      if (!published && !force) return Promise.resolve();
      published = false;
      const body = { clientId };
      const base = process.env.REACT_APP_BACKEND_URL;
      if (token && base && typeof fetch === "function") {
        // Bypass the global Axios 401 handler: cleanup with an old account token
        // must never clear a newer login in another tab. Keepalive also covers close.
        changed();
        return fetch(`${base}/api${ENDPOINT}`, {
          method: "DELETE", keepalive: !!keepalive, headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch(() => {});
      }
      const pending = api.delete(ENDPOINT, { data: body, headers }).catch(() => {});
      changed();
      return pending;
    };

    const locate = () => {
      if (!active || !visible || denied || locating || uploading) return;
      const requestGeneration = generation;
      locating = true;
      navigator.geolocation.getCurrentPosition((position) => {
        locating = false;
        if (!active || !visible || denied) return;
        if (requestGeneration !== generation) { locate(); return; }
        const { latitude: lat, longitude: lng, accuracy } = position.coords || {};
        const age = Date.now() - position.timestamp;
        // The browser may return a cached point despite maximumAge=0. Never keep
        // renewing a colleague's location with an old position.
        if (!Number.isFinite(position.timestamp) || age > MAX_POSITION_AGE_MS || age < -MAX_POSITION_AGE_MS
          || !Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180
          || !Number.isFinite(accuracy) || accuracy <= 0) return;
        uploading = true;
        api.put(ENDPOINT, { clientId, lat, lng, accuracy }, { headers }).then(() => {
          if (!active || !visible || requestGeneration !== generation) return remove(false, true);
          published = true;
          changed();
          return undefined;
        }).catch(() => {}).finally(() => {
          uploading = false;
          if (active && visible && requestGeneration !== generation) locate();
        });
      }, (error) => {
        locating = false;
        if (error?.code === 1) {
          generation += 1;
          denied = true; // Permission denied: do not prompt again on each tick.
          remove();
        }
      }, { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 });
    };

    const onVisibility = () => {
      generation += 1;
      visible = document.visibilityState !== "hidden";
      if (visible) locate();
      else remove();
    };
    const onPageHide = () => { generation += 1; visible = false; remove(true); };
    const onPermissionChange = () => {
      if (!permission) return;
      generation += 1;
      // A transition back to `prompt` also revokes the previous grant.
      denied = permission.state !== "granted";
      if (denied) remove();
      else if (permission.state === "granted") locate();
    };
    const onAuthStorage = (event) => {
      if (event.key !== "ali_token" || !token || event.oldValue !== token || event.newValue === token) return;
      // Another tab signed out or replaced this login; stop using the captured
      // token even if this tab has not rerendered yet.
      active = false;
      generation += 1;
      window.clearInterval(timer);
      remove(false, true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("storage", onAuthStorage);
    const timer = window.setInterval(locate, UPDATE_MS);
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((status) => {
        if (!active) return;
        permission = status;
        status.addEventListener?.("change", onPermissionChange);
        denied = status.state === "denied";
        if (!denied) locate();
      }).catch(locate);
    } else locate();

    return () => {
      active = false;
      generation += 1;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("storage", onAuthStorage);
      permission?.removeEventListener?.("change", onPermissionChange);
      remove(false, true);
    };
  }, [api, username, eligible, paused]);
  return paused;
}
